import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

const ClientSearchSelect = ({ 
  value, 
  onChange, 
  options = [], 
  placeholder = "Seleccionar cliente",
  maxDisplayItems = 5,
  searchPlaceholder = "Buscar cliente..."
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Normalizar texto para búsqueda
  const normalizeText = (text) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  // Filtrar opciones basado en el término de búsqueda
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOptions(options);
    } else {
      const normalizedSearch = normalizeText(searchTerm);
      const filtered = options.filter(option => 
        normalizeText(option.label).includes(normalizedSearch)
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options]);

  // Encontrar la opción seleccionada
  useEffect(() => {
    const selected = options.find(option => option.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Enfocar el input de búsqueda cuando se abre el dropdown
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onChange('');
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div className="client-search-select" ref={dropdownRef}>
      <div className="client-search-trigger" onClick={handleToggle}>
        <div className="client-search-display">
          {selectedOption ? (
            <div className="client-search-selected">
              <span className="client-search-name">{selectedOption.label}</span>
            </div>
          ) : (
            <span className="client-search-placeholder">{placeholder}</span>
          )}
        </div>
        <div className="client-search-actions">
          {selectedOption && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="client-search-clear"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown 
            size={16} 
            className={`client-search-chevron ${isOpen ? 'open' : ''}`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="client-search-dropdown">
          <div className="client-search-input-container">
            <Search size={16} className="client-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder={searchPlaceholder}
              className="client-search-input"
            />
          </div>
          
          <div className="client-search-options">
            {filteredOptions.length === 0 ? (
              <div className="client-search-no-results">
                No se encontraron clientes
              </div>
            ) : (
              <>
                {filteredOptions.slice(0, maxDisplayItems).map((option) => (
                  <div
                    key={option.value}
                    onClick={() => handleSelect(option)}
                    className={`client-search-option ${
                      value === option.value ? 'selected' : ''
                    }`}
                  >
                    <span className="client-search-option-label">
                      {option.label}
                    </span>
                  </div>
                ))}
                
                {filteredOptions.length > maxDisplayItems && (
                  <div className="client-search-more">
                    <div className="client-search-scroll-indicator">
                      {filteredOptions.length - maxDisplayItems} más...
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSearchSelect;
